// src/pages/UserFunds.jsx
import React, { useEffect, useState, useContext, useMemo } from "react";
import { api } from "../lib/api";
import { AuthContext } from "../authContext";
import LoadingModal from "../components/LoadingModal";
import "../user/Dashboard.css";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

// 🔐 mark who is admin here (or pull from your auth/role claims)
const ADMIN_IDS = new Set([
  "pziPDtOgJiX06oGuJxNwxE", // your current admin sanityId/userId
]);

export default function UserFunds() {
  const { userId } = useContext(AuthContext);
  const isAdmin = useMemo(() => ADMIN_IDS.has(userId), [userId]);

  const [bots, setBots] = useState([]);
  const [botId, setBotId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // 1) Load all bots (for admin selector, or fallback when latest-bot fails)
  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/api/fund-sharing/bots");
        const list = res.data?.bots || [];
        setBots(list);

        // If admin, default to first bot (can be changed via selector)
        if (isAdmin && list.length && !botId) {
          setBotId(list[0]._id);
        }
      } catch (e) {
        setError(e?.response?.data?.error || e.message);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  // 2) If NOT admin, try to auto-pick the latest bot the user contributed to
  useEffect(() => {
    if (!userId || isAdmin || botId) return;
    (async () => {
      try {
        // Try endpoint #1
        let latest = null;
        try {
          const r1 = await api.get("/api/fund-sharing/user/latest-bot", {
            params: { userId },
          });
          latest = r1?.data?.botId || null;
        } catch {
          // Try endpoint #2 (fallback, if you expose it)
          try {
            const r2 = await api.get("/api/fund-sharing/contributions/latest", {
              params: { userId },
            });
            latest = r2?.data?.botId || null;
          } catch {
            // ignore; we’ll fallback to first bot below
          }
        }

        if (latest) {
          setBotId(latest);
        } else if (bots.length) {
          // graceful fallback: first bot configured
          setBotId(bots[0]._id);
        }
      } catch (e) {
        // If this fails badly, still fallback to first bot
        if (bots.length) setBotId(bots[0]._id);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isAdmin, bots]);

  // 3) Load user’s fund view once we have both IDs
  useEffect(() => {
    if (!userId || !botId) return;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get("/api/fund-sharing/me", {
          params: { botId },
          headers: { "X-User-Id": userId },
        });
        setData(res.data);
        setError("");
      } catch (e) {
        setError(e?.response?.data?.error || e.message);
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId, botId]);

  if (!bots.length) {
    return (
      <div className="dashboard-main">
        <div className="outcome-table-wrapper-dashboard">
          <div className="outcome-table">
            No bots configured yet. Ask admin to add one.
          </div>
        </div>
      </div>
    );
  }

  if (loading || !data) {
    return <LoadingModal text="Loading your fund status..." />;
  }

  return (
    <div className="dashboard-main">
      <div className="outcome-table-wrapper-dashboard">
        <div className="expanded-content" style={{ gap: 16 }}>
          {/* Admin-only: bot chooser */}
          {isAdmin && (
            <div className="expanded-section">
              <h3>🤖 Select Bot</h3>
              <select
                className="date-input-wallet"
                value={botId}
                onChange={(e) => setBotId(e.target.value)}
                style={{ maxWidth: 320 }}
              >
                {bots.map((b) => (
                  <option key={b._id} value={b._id}>
                    {b.name} ({b.exchange})
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="expanded-section">
            <h3>👤 Your Allocation</h3>
            <p>
              <strong>Net Contribution:</strong> {money(data.net)}
            </p>
            <p>
              <strong>Your Share:</strong>{" "}
              {Number(data.sharePct || 0).toFixed(2)}%
            </p>
          </div>

          <div className="expanded-section">
            <h3>🤖 Bot</h3>
            <p>
              <strong>Bot Trading Amount:</strong>{" "}
              {money(data.bot?.tradingAmount)}
            </p>
            <p><strong>Bot Current Value:</strong> {money(data.bot.currentValue)}</p>
            <p><strong>Invested (positions):</strong> {money(data.bot.investedInitialMargin)}</p>
            <p><strong>Allocated (open orders):</strong> {money(data.bot.openOrderMargin)}</p>
            <p><strong>Remaining (free):</strong> {money(data.bot.availableBalance)}</p>
            
            <p>
              <strong>Period:</strong> {data.bot?.periodLabel || "-"}
            </p>
            <p>
              <strong>Bot Realized PnL:</strong>{" "}
              {money(data.bot?.periodRealizedPnl)}
            </p>
            <p>
              <strong>Your Profit:</strong>{" "}
              <span style={{ fontWeight: 700 }}>{money(data.periodProfit)}</span>
            </p>
           <p><small>Synced: {data.bot.lastSyncAt ? new Date(data.bot.lastSyncAt).toLocaleString() : '-'}</small></p>
          </div>
        </div>

        <div className="outcome-table">
          <h3>📒 Your Ledger</h3>
          <div className="table-scroll-container">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Type</th>
                  <th>Amount</th>
                </tr>
              </thead>
              <tbody>
                {(data.ledger || []).map((row) => (
                  <tr key={row.id}>
                    <td>{new Date(row.createdAt).toLocaleString()}</td>
                    <td style={{ textTransform: "capitalize" }}>{row.type}</td>
                    <td>{money(row.amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {error && (
            <div style={{ color: "#f66", padding: 12 }}>{String(error)}</div>
          )}
        </div>
      </div>
    </div>
  );
}
